import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAppStore } from "../../../../../GlobalStore/GlobalStore.ts";
import { FileUploadItem } from './Items/FileUploadItem';
import { UploadItemConfig, CommonProps } from './types/UploadItem';
import {isIde, isMobileApp} from '../../../../../Utils/misc/envNameUtils.ts';

interface UploadMenuProps {
    buttonRef: React.RefObject<HTMLButtonElement | null>;
}

export const UploadMenu: React.FC<UploadMenuProps> = ({ buttonRef }) => {
    const [portalElement] = useState(() => document.createElement('div'));
    const menuRef = useRef<HTMLDivElement>(null);

    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const {environment} = environmentObj;
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const {theme} = settingsObj;
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);

    useEffect(() => {
        document.body.appendChild(portalElement);
        return () => {
            document.body.removeChild(portalElement);
        };
    }, [portalElement]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const menuElement = menuRef.current;
            const uploadButton = buttonRef.current;

            if (!menuElement?.contains(target) && target !== uploadButton && !uploadButton?.contains(target)) {
                setDynamicStateObj({isUploadOpen: false});
            }
        };

        if (dynamicStateObj.isUploadOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dynamicStateObj.isUploadOpen]);

    const [position, setPosition] = useState({ left: 0, top: 0 });

    useEffect(() => {
        const updatePosition = () => {
            const uploadButton = buttonRef.current;
            if (!uploadButton) return;

            const rect = uploadButton.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            
            // Calculate position to ensure menu stays on screen
            const buttonCenter = rect.left + (rect.width / 2);
            
            setPosition({
                left: buttonCenter - 18, // Center the menu with slight offset
                top: Math.min(rect.top, windowHeight - 50) // Ensure it doesn't go below screen
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition);
        };
    }, [buttonRef]);

    const menuItems: UploadItemConfig[] = [
        { type: 'audio' },
        { type: 'documents' },
        { type: 'camera' },
        { type: 'photos' },
        { type: 'upload' }
    ];

    const buttonClasses = theme === 'dark'
        ? "p-2 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-all duration-300 w-10 h-10 flex items-center justify-center shadow-xl hover:shadow-2xl"
        : "p-2 rounded-full bg-neutral-200 hover:bg-neutral-300 transition-all duration-300 w-10 h-10 flex items-center justify-center shadow-lg hover:shadow-xl";

    const iconClasses = theme === 'dark'
        ? "text-neutral-200"
        : "text-neutral-700";

    const transitionClasses = dynamicStateObj.isUploadOpen
        ? "opacity-100 translate-y-0 pointer-events-auto"
        : "opacity-0 translate-y-2.5 pointer-events-none";

    const commonProps: CommonProps = {
        theme,
        buttonClasses,
        iconClasses,
    };

    const getComponentForItem = (item: UploadItemConfig, index: number) => {
        if (isMobileApp(environment)) {
            switch (item.type) {
                case "photos":
                    return <FileUploadItem key={index} {...commonProps} accept="image/*,video/*" label="Photos" />;
                case "camera":
                    return <FileUploadItem key={index} {...commonProps} accept="image/*,video/*" capture="environment" label="Camera" />;
                case "documents":
                    return <FileUploadItem key={index} {...commonProps} accept=".pdf,.doc,.docx,.txt,.rtf" label="Documents" />;
                default:
                    return null;
            }
        }
        else if (environment === "web" || isIde(environment)) {
            switch (item.type) {
                case "upload":
                    return <FileUploadItem key={index} {...commonProps} label="Upload" />;
                default:
                    return null;
            }
        }
        else {
            switch (item.type) {
                case "upload":
                    return <FileUploadItem key={index} {...commonProps} label="Upload" />;
                default:
                    return null;
            }
        }
    };

    return createPortal(
        <div
            className={`fixed transition-all duration-300 ease-in-out ${transitionClasses}`}
            style={{top: `${position.top}px`, left: `${position.left}px`}}>
            <div
                ref={menuRef}
                className={`flex flex-col gap-3 max-h-[80vh] overflow-y-auto py-2`}
                style={{
                    maxWidth: '200px',
                    transform: 'translateY(-100%)',
                    marginTop: '-10px'
                }}
                onClick={e => e.stopPropagation()}
            >
                {menuItems.map((item, index) => getComponentForItem(item, index))}

            </div>
        </div>,
        portalElement
    );
};